import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { User } from '../types';

interface UseNewsNotificationsReturn {
  unreadNewsCount: number;
}

export const useNewsNotifications = (currentUser: User | null, pathname: string): UseNewsNotificationsReturn => {
  const [unreadNewsCount, setUnreadNewsCount] = useState(0);

  // News unread check
  useEffect(() => {
    if (!currentUser) return;

    const qNews = query(collection(db, 'news'));
    const unsubscribeNews = onSnapshot(qNews, (snapshot) => {
      const lastReadTime = currentUser.lastReadNewsTime ? new Date(currentUser.lastReadNewsTime).getTime() : 0;
      let count = 0;
      snapshot.docs.forEach(doc => {
        const postDate = new Date(doc.data().date).getTime();
        if (postDate > lastReadTime) {
          count++;
        }
      });
      setUnreadNewsCount(count);
    });

    return () => unsubscribeNews();
  }, [currentUser?.lastReadNewsTime, currentUser?.id]);

  // Update last read time when entering news tab
  useEffect(() => {
    if (pathname === '/news' && currentUser && unreadNewsCount > 0) {
      const updateLastRead = async () => {
        const now = new Date().toISOString();
        try {
          await updateDoc(doc(db, 'users', currentUser.id), {
            lastReadNewsTime: now
          });
          // Optimistically update local state to clear badge immediately
          setUnreadNewsCount(0);
        } catch (error) {
          console.error("Error updating last read time:", error);
        }
      };
      // Small delay to ensure user actually saw the page
      const timer = setTimeout(updateLastRead, 1000);
      return () => clearTimeout(timer);
    }
  }, [pathname, currentUser, unreadNewsCount]);

  return { unreadNewsCount };
};
